// Login/auth screen — ported from ui.ts auth-view + key-reveal-overlay.
// Handles: generate key, nsec login, NIP-07 extension, saved accounts.

import { useState, useEffect, useCallback } from 'preact/hooks';
import { nsecEncode, npubEncode } from 'nostr-tools/nip19';
import { getPublicKey } from 'nostr-tools/pure';
import {
  generateKey,
  decodeNsec,
  signAuthEvent,
  signAuthEventWithExtension,
  isExtensionAvailable,
  getAccounts,
  saveAccount,
  removeAccount,
  migrateOldKey,
  LAST_ACCOUNT_KEY,
} from '../lib/nostr';
import { bytesToHex } from '../lib/crypto';
import { auth as authApi } from '../lib/api';
import type { Session, SavedAccount } from '../lib/types';
import { useStore } from '../lib/store';

interface LoginProps {
  domain: string;
  onAuthenticated: (session: Session) => void;
}

export function Login({ domain, onAuthenticated }: LoginProps) {
  const { showToast } = useStore();
  const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>([]);
  const [extensionAvailable, setExtensionAvailable] = useState(false);
  const [showExtensionMissing, setShowExtensionMissing] = useState(false);
  const [nsecInput, setNsecInput] = useState('');
  const [pubkeyDisplay, setPubkeyDisplay] = useState('');

  // Key reveal overlay state
  const [keyOverlayOpen, setKeyOverlayOpen] = useState(false);
  const [generatedNsec, setGeneratedNsec] = useState('');
  const [storeKeyCheck, setStoreKeyCheck] = useState(true);
  const [confirmHint, setConfirmHint] = useState('Copy or download your key, then confirm below.');
  const [pendingSecretKey, setPendingSecretKey] = useState<Uint8Array | null>(null);

  // Refresh saved accounts list
  const refreshAccounts = useCallback(() => {
    setSavedAccounts(getAccounts());
  }, []);

  // Detect NIP-07 extension (with retry for slow injection)
  useEffect(() => {
    if (isExtensionAvailable()) {
      setExtensionAvailable(true);
      return;
    }
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (isExtensionAvailable()) {
        setExtensionAvailable(true);
        setShowExtensionMissing(false);
        clearInterval(interval);
      } else if (tries > 20) {
        setShowExtensionMissing(true);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Init: migrate old key, render saved accounts, auto-login
  useEffect(() => {
    migrateOldKey();
    refreshAccounts();

    const lastPubkey = localStorage.getItem(LAST_ACCOUNT_KEY);
    if (lastPubkey) {
      const account = getAccounts().find((a) => a.pubkey === lastPubkey);
      if (account) {
        try {
          const decoded = decodeNsec(account.nsec);
          setPubkeyDisplay(account.npub);
          void doAuthWithSecretKey(decoded.secretKey);
        } catch {
          // ignore
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Auth flow with local nsec ───

  const doAuthWithSecretKey = useCallback(
    async (secretKey: Uint8Array): Promise<void> => {
      try {
        const { nonce } = await authApi.challenge();
        const event = signAuthEvent(secretKey, nonce);
        const data = await authApi.verify(event);
        const session: Session = {
          pubkey: data.pubkey,
          secretKeyHex: bytesToHex(secretKey),
          authMethod: 'nsec',
        };
        onAuthenticated(session);
      } catch (e) {
        showToast('Auth error: ' + e, 'error');
      }
    },
    [onAuthenticated, showToast],
  );

  // ─── Generate new key ───

  const handleGenerate = useCallback(() => {
    const key = generateKey();
    setPendingSecretKey(key.secretKey);
    setGeneratedNsec(key.nsec);
    setConfirmHint('Copy or download your key, then confirm below.');
    setStoreKeyCheck(true);
    setKeyOverlayOpen(true);
  }, []);

  // ─── Confirm key saved ───

  const handleConfirmKeySaved = useCallback(async () => {
    if (!pendingSecretKey) {
      showToast('No key generated', 'error');
      return;
    }
    const sk = pendingSecretKey;
    const ns = nsecEncode(sk);
    const pubkey = getPublicKey(sk);
    const npub = npubEncode(pubkey);

    setPendingSecretKey(null);
    if (storeKeyCheck) {
      saveAccount(pubkey, ns);
    }
    setPubkeyDisplay(npub);
    setKeyOverlayOpen(false);
    localStorage.setItem(LAST_ACCOUNT_KEY, pubkey);
    await doAuthWithSecretKey(sk);
  }, [pendingSecretKey, storeKeyCheck, doAuthWithSecretKey, showToast]);

  // ─── nsec login ───

  const handleLoginWithNsec = useCallback(async () => {
    const nsec = nsecInput.trim();
    if (!nsec.startsWith('nsec1')) {
      showToast('Invalid nsec', 'error');
      return;
    }
    try {
      const decoded = decodeNsec(nsec);
      setPubkeyDisplay(decoded.npub);
      saveAccount(decoded.pubkey, nsec);
      localStorage.setItem(LAST_ACCOUNT_KEY, decoded.pubkey);
      await doAuthWithSecretKey(decoded.secretKey);
    } catch (e) {
      showToast('Failed to decode nsec: ' + e, 'error');
    }
  }, [nsecInput, doAuthWithSecretKey, showToast]);

  // ─── Saved account login ───

  const handleSavedAccountLogin = useCallback(
    async (nsec: string) => {
      try {
        const decoded = decodeNsec(nsec);
        setPubkeyDisplay(decoded.npub);
        localStorage.setItem(LAST_ACCOUNT_KEY, decoded.pubkey);
        await doAuthWithSecretKey(decoded.secretKey);
      } catch (e) {
        showToast('Failed to sign in: ' + e, 'error');
      }
    },
    [doAuthWithSecretKey, showToast],
  );

  // ─── Delete saved account ───

  const handleDeleteAccount = useCallback(
    (pubkey: string) => {
      removeAccount(pubkey);
      refreshAccounts();
      showToast('Account removed', '');
    },
    [refreshAccounts, showToast],
  );

  // ─── Extension login ───

  const handleExtensionLogin = useCallback(async () => {
    if (!isExtensionAvailable()) {
      showToast('No Nostr extension found.', 'error');
      return;
    }
    const NIP07_TIMEOUT = 30_000;
    const withTimeout = <T,>(p: Promise<T>, ms: number, label: string): Promise<T> =>
      Promise.race([
        p,
        new Promise<T>((_, reject) =>
          setTimeout(
            () => reject(new Error(label + ' timed out — the extension may be waiting for user approval')),
            ms,
          ),
        ),
      ]);

    try {
      showToast('Requesting public key from extension...', '');
      const pk = await withTimeout(window.nostr!.getPublicKey(), NIP07_TIMEOUT, 'getPublicKey');
      setPubkeyDisplay(pk);

      const { nonce } = await authApi.challenge();
      showToast('Please approve the signing request in your extension...', '');
      const event = await withTimeout(
        signAuthEventWithExtension(nonce),
        NIP07_TIMEOUT,
        'signEvent',
      );
      const data = await authApi.verify(event);

      const session: Session = {
        pubkey: data.pubkey,
        secretKeyHex: null,
        authMethod: 'extension',
      };
      localStorage.setItem(LAST_ACCOUNT_KEY, data.pubkey);
      onAuthenticated(session);
    } catch (e) {
      const msg = String(e);
      if (msg.includes('timed out')) {
        showToast('Extension timed out. If using Amber via bridge, make sure the app is open.', 'error');
      } else if (msg.includes('reject') || msg.includes('denied') || msg.includes('cancel')) {
        showToast('Request rejected by extension.', 'error');
      } else {
        showToast('Extension auth error: ' + msg, 'error');
      }
    }
  }, [onAuthenticated, showToast]);

  // ─── Copy / download generated nsec ───

  const copyGeneratedNsec = useCallback(() => {
    navigator.clipboard.writeText(generatedNsec).then(() => {
      showToast('Key copied to clipboard!', 'success');
      setConfirmHint('Key copied! Paste it somewhere safe before continuing.');
    });
  }, [generatedNsec, showToast]);

  const downloadGeneratedNsec = useCallback(() => {
    const blob = new Blob([generatedNsec + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nomail-nsec.txt';
    a.click();
    URL.revokeObjectURL(url);
    showToast('Key file downloaded!', 'success');
    setConfirmHint('File downloaded! Store it somewhere secure.');
  }, [generatedNsec, showToast]);

  return (
    <>
      <div class="auth-overlay" data-testid="login-screen">
        <div class="auth-card">
          <h1 style="font-size:20px;margin-bottom:8px;">Welcome to {domain}</h1>
          <p>Nostr-verified email. Sign in with your Nostr identity.</p>

          {savedAccounts.length > 0 && (
            <div class="saved-accounts">
              <div style="font-size:11px;color:var(--muted);margin-bottom:8px;">SAVED ACCOUNTS</div>
              {savedAccounts.map((a) => (
                <div
                  key={a.pubkey}
                  class="account-row"
                  onClick={() => handleSavedAccountLogin(a.nsec)}
                >
                  <span class="account-npub">
                    {a.npub.substring(0, 16)}...{a.npub.substring(a.npub.length - 10)}
                  </span>
                  <span
                    class="account-del"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAccount(a.pubkey);
                    }}
                  >
                    &times;
                  </span>
                </div>
              ))}
            </div>
          )}

          <button
            class="btn"
            style="width:100%;margin-bottom:12px"
            onClick={handleGenerate}
            data-testid="generate-identity-btn"
          >
            Generate New Identity
          </button>

          <div class="auth-divider">or use a Nostr extension</div>

          {extensionAvailable ? (
            <button
              class="btn btn-ghost"
              style="width:100%;margin-bottom:8px"
              onClick={handleExtensionLogin}
              data-testid="extension-login-btn"
            >
              Sign In with Nostr Extension
            </button>
          ) : showExtensionMissing ? (
            <div style="font-size:13px;color:var(--muted);text-align:center;line-height:2;">
              No extension detected. Install one:<br />
              <a href="https://getalby.com" target="_blank" rel="noopener" style="color:var(--accent)">Alby</a> &middot;
              <a href="https://github.com/jiftechnify/nos2x" target="_blank" rel="noopener" style="color:var(--accent)">nos2x</a> &middot;
              <a href="https://nostore.app" target="_blank" rel="noopener" style="color:var(--accent)">Nostore</a>
            </div>
          ) : null}

          <details class="nsec-warning-collapsible">
            <summary>Sign in with nsec (advanced)</summary>
            <div class="nsec-warning">
              <strong>Pasting your nsec is risky.</strong>
              Only do this if you understand the risks. Anyone with this key has full access to your email. Consider using a{' '}
              <a href="https://getalby.com" target="_blank" rel="noopener">Nostr extension</a> instead.
            </div>

            <div class="field">
              <textarea
                placeholder="Paste your nsec (nsec1...)"
                value={nsecInput}
                onInput={(e) => setNsecInput((e.target as HTMLTextAreaElement).value)}
                data-testid="nsec-input"
              />
            </div>

          <button
            class="btn btn-ghost"
            style="width:100%"
            onClick={handleLoginWithNsec}
            data-testid="nsec-login-btn"
          >
            Sign In with nsec
          </button>

          {pubkeyDisplay && (
            <div class="npub-display">{pubkeyDisplay}</div>
          )}
          </details>
        </div>
      </div>

      {/* Key Reveal Overlay */}
      {keyOverlayOpen && (
        <div class="compose-overlay active" role="dialog" aria-modal="true" aria-label="Save identity key" data-testid="key-dialog">
          <div class="key-reveal-card">
            <h2>Save Your Identity</h2>
            <div class="key-warning">
              <strong> This is your only key — save it now! </strong>
              <p>
                This nsec is your <em>only</em> way to access your inbox. If you lose it,{' '}
                <em>your account cannot be recovered</em> — there is no password reset, no email recovery, no support team to contact.
              </p>
            </div>
            <div class="key-tagline">Not your key, not your email.</div>
            <div class="key-display">
              <code data-testid="generated-nsec">{generatedNsec}</code>
            </div>
            <div class="key-actions">
              <button class="btn btn-ghost" onClick={copyGeneratedNsec} data-testid="copy-key-btn">Copy Key</button>
              <button class="btn btn-ghost" onClick={downloadGeneratedNsec} data-testid="download-key-btn">Download</button>
            </div>
            <label class="key-store-opt">
              <input
                type="checkbox"
                checked={storeKeyCheck}
                onChange={(e) => setStoreKeyCheck((e.target as HTMLInputElement).checked)}
                data-testid="remember-me-checkbox"
              />
              <span>
                Remember me on this device
                <small>Stores your key in this browser for automatic login. Uncheck if using a shared computer.</small>
              </span>
            </label>
            <div class="key-confirm-note">{confirmHint}</div>
            <div class="compose-actions">
              <button
                class="btn btn-ghost"
                onClick={() => {
                  setPendingSecretKey(null);
                  setKeyOverlayOpen(false);
                }}
                data-testid="cancel-key-btn"
              >
                Cancel
              </button>
              <button class="btn" onClick={handleConfirmKeySaved} data-testid="save-key-btn">I've Saved My Key</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
